import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

function parseTimeRangeValue(value) {
  const trimmed = String(value ?? '').trim();
  const match = trimmed.match(/^([01]\d|2[0-3]):([0-5]\d)-([01]\d|2[0-3]):([0-5]\d)$/);

  if (!match) {
    return { start: '', end: '' };
  }

  return {
    start: `${match[1]}:${match[2]}`,
    end: `${match[3]}:${match[4]}`,
  };
}

function parseMultiSelectValue(value, options = []) {
  const canonicalByToken = new Map();
  options.forEach(option => {
    const optionValue = typeof option === 'object' ? option.value : option;
    const optionLabel = typeof option === 'object' ? option.label : option;
    const aliases = [optionValue, optionLabel, ...(option.aliases || [])];
    aliases.forEach(alias => {
      canonicalByToken.set(String(alias).trim().toLowerCase(), optionValue);
    });
  });

  return String(value ?? '')
    .split(/[,&/]+|\s+/)
    .map(part => part.trim().toLowerCase())
    .filter(Boolean)
    .map(part => canonicalByToken.get(part))
    .filter(Boolean)
    .filter((part, index, list) => list.indexOf(part) === index);
}

function normalizeEditableValue(value) {
  if (typeof value === 'number' && Number.isNaN(value)) return '';
  if (typeof value === 'string' && ['nan', 'nat'].includes(value.trim().toLowerCase())) return '';
  return value ?? '';
}

function isEmptyEditableValue(value, type, inputProps = {}) {
  const normalizedValue = normalizeEditableValue(value);
  if (normalizedValue === '') return true;
  if (type !== 'multi-select') return false;

  const normalized = String(normalizedValue).trim().toLowerCase();
  if (!normalized || ['none', 'n/a', 'na', 'no', 'no day off', 'no days off', '--'].includes(normalized)) {
    return true;
  }

  return parseMultiSelectValue(value, inputProps.options).length === 0;
}

export default function EditableCell({
  value,
  type = 'text',
  onSave,
  inputProps = {},
  validateAndNormalize,
  emptyLabel = 'Click to add',
  className = '',
  readOnly = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(() => normalizeEditableValue(value));
  const [rangeDraft, setRangeDraft] = useState(() => parseTimeRangeValue(value));
  const [multiDraft, setMultiDraft] = useState(() => parseMultiSelectValue(value, inputProps.options));
  const cleanupRef            = useRef(null);
  const displayRef            = useRef(null);
  const [validationError, setValidationError] = useState('');
  const [rangeEditorPosition, setRangeEditorPosition] = useState(null);

  // Whenever the prop value changes externally, reset our draft
  useEffect(() => {
    if (!editing) {
      setDraft(normalizeEditableValue(value));
      setRangeDraft(parseTimeRangeValue(value));
      setMultiDraft(parseMultiSelectValue(value, inputProps.options));
      setValidationError('');
    }
  }, [value, editing, inputProps.options]);

  // When we enter edit mode, push a history entry and listen for popstate
  useEffect(() => {
    if (editing) {
      // Push a dummy state so back button will trigger popstate
      window.history.pushState({ editingCell: true }, '');

      const handlePop = (e) => {
        // Cancel editing instead of navigating
        e.preventDefault();
        cancelEdit();
      };
      window.addEventListener('popstate', handlePop);

      // Store cleanup so we can remove listener later
      cleanupRef.current = () => {
        window.removeEventListener('popstate', handlePop);
        // Remove our dummy state so history is normal
        window.history.back();
      };
    }
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [editing]);

  const cancelEdit = () => {
    setEditing(false);
    setDraft(normalizeEditableValue(value));
    setRangeDraft(parseTimeRangeValue(value));
    setMultiDraft(parseMultiSelectValue(value, inputProps.options));
    setValidationError('');
    setRangeEditorPosition(null);
  };

  const updateRangeEditorPosition = () => {
    if (!['time-range', 'multi-select'].includes(type) || !displayRef.current) return;
    const rect = displayRef.current.getBoundingClientRect();
    const editorWidth = type === 'multi-select' ? 260 : 286;
    const editorHeight = type === 'multi-select' ? 260 : 126;
    const margin = 12;
    const left = Math.min(
      Math.max(rect.left, margin),
      Math.max(margin, window.innerWidth - editorWidth - margin)
    );
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < editorHeight + margin
      ? Math.max(margin, rect.top - editorHeight - 8)
      : rect.bottom + 8;

    setRangeEditorPosition({ top, left, width: editorWidth });
  };

  const startEdit = () => {
    if (readOnly) return;
    setDraft(normalizeEditableValue(value));
    setRangeDraft(parseTimeRangeValue(value));
    setValidationError('');
    setEditing(true);
    if (['time-range', 'multi-select'].includes(type)) {
      window.requestAnimationFrame(updateRangeEditorPosition);
    }
  };

  const attemptSave = () => {
    let nextValue = type === 'time-range'
      ? (rangeDraft.start || rangeDraft.end ? `${rangeDraft.start}-${rangeDraft.end}` : '')
      : type === 'multi-select'
        ? multiDraft.join(',')
      : draft;

    if (type === 'time-range' && (rangeDraft.start || rangeDraft.end) && (!rangeDraft.start || !rangeDraft.end)) {
      setValidationError('Enter both start and end times');
      return;
    }

    if (type === 'multi-select' && inputProps.maxSelections && multiDraft.length > inputProps.maxSelections) {
      setValidationError(`Choose up to ${inputProps.maxSelections}`);
      return;
    }

    if (validateAndNormalize) {
      const result = validateAndNormalize(nextValue);
      if (!result?.isValid) {
        setValidationError(result?.message || 'Invalid value');
        return;
      }
      nextValue = result.value;
    }

    if (nextValue !== value) {
      onSave(nextValue);
    }
    setValidationError('');
    setEditing(false);
    setRangeEditorPosition(null);
  };

  useEffect(() => {
    if (!editing || !['time-range', 'multi-select'].includes(type)) return undefined;

    updateRangeEditorPosition();
    window.addEventListener('resize', updateRangeEditorPosition);
    window.addEventListener('scroll', updateRangeEditorPosition, true);

    return () => {
      window.removeEventListener('resize', updateRangeEditorPosition);
      window.removeEventListener('scroll', updateRangeEditorPosition, true);
    };
  }, [editing, type]);

  const isEmpty = isEmptyEditableValue(value, type, inputProps);
  const displayValue = normalizeEditableValue(value);
  const displayClassName = [
    'editable-cell',
    isEmpty ? 'editable-cell--empty' : '',
    editing && ['time-range', 'multi-select'].includes(type) ? 'editable-cell--active' : '',
    className,
    readOnly ? 'editable-cell--readonly' : '',
  ].filter(Boolean).join(' ');

  const displayCell = (
    <div
      ref={displayRef}
      className={displayClassName}
      onClick={startEdit}
      style={{
        cursor: readOnly ? 'default' : 'pointer',
        display: 'block',
        minHeight: '24px',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        textAlign: 'center',
        padding: '4px',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}
    >
      {type === 'multi-select' && !isEmpty ? (
        <span className="editable-cell__stacked-value">
          {parseMultiSelectValue(value, inputProps.options).map(item => (
            <span key={item}>{item}</span>
          ))}
        </span>
      ) : (
        isEmpty ? (readOnly ? '' : emptyLabel) : displayValue
      )}
    </div>
  );

  if (editing) {
    if (type === 'select') {
      const options = Array.isArray(inputProps.options) ? inputProps.options : [];
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          <select
            value={draft}
            autoFocus
            onChange={e => {
              setDraft(e.target.value);
              if (validationError) setValidationError('');
            }}
            onBlur={() => {
              attemptSave();
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                attemptSave();
              }
              else if (e.key === 'Escape') {
                cancelEdit();
              }
            }}
            style={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              textAlign: 'center',
              boxSizing: 'border-box',
              borderColor: validationError ? '#c62828' : undefined,
              boxShadow: validationError ? '0 0 0 1px rgba(198, 40, 40, 0.25)' : undefined,
            }}
          >
            <option value=""></option>
            {options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          {validationError ? (
            <div
              role="alert"
              style={{
                color: '#c62828',
                fontSize: '0.72rem',
                lineHeight: 1.2,
                marginTop: '4px',
                textAlign: 'center',
                whiteSpace: 'normal',
              }}
            >
              {validationError}
            </div>
          ) : null}
        </div>
      );
    }

    if (type === 'time-range') {
      return (
        <>
          {displayCell}
          {rangeEditorPosition ? ReactDOM.createPortal(
            <div
              className="editable-cell__range editable-cell__range-popover"
              style={{
                top: rangeEditorPosition.top,
                left: rangeEditorPosition.left,
                width: rangeEditorPosition.width,
              }}
            >
              <div className="editable-cell__range-inputs">
                <input
                  type="time"
                  step={60}
                  value={rangeDraft.start}
                  autoFocus
                  onChange={e => {
                    setRangeDraft(current => ({ ...current, start: e.target.value }));
                    if (validationError) setValidationError('');
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      attemptSave();
                    } else if (e.key === 'Escape') {
                      cancelEdit();
                    }
                  }}
                />
                <span className="editable-cell__range-separator">to</span>
                <input
                  type="time"
                  step={60}
                  value={rangeDraft.end}
                  onChange={e => {
                    setRangeDraft(current => ({ ...current, end: e.target.value }));
                    if (validationError) setValidationError('');
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      attemptSave();
                    } else if (e.key === 'Escape') {
                      cancelEdit();
                    }
                  }}
                />
              </div>
              <div className="editable-cell__range-actions">
                <button type="button" onClick={attemptSave}>Save</button>
                <button type="button" onClick={cancelEdit}>Cancel</button>
              </div>
              {validationError ? (
                <div
                  role="alert"
                  style={{
                    color: '#c62828',
                    fontSize: '0.72rem',
                    lineHeight: 1.2,
                    marginTop: '4px',
                    textAlign: 'center',
                    whiteSpace: 'normal',
                  }}
                >
                  {validationError}
                </div>
              ) : null}
            </div>,
            document.body
          ) : null}
        </>
      );
    }

    if (type === 'multi-select') {
      const options = Array.isArray(inputProps.options) ? inputProps.options : [];
      const maxSelections = inputProps.maxSelections || options.length;
      const toggleOption = optionValue => {
        setMultiDraft(current => {
          if (current.includes(optionValue)) {
            return current.filter(value => value !== optionValue);
          }
          if (current.length >= maxSelections) {
            setValidationError(`Choose up to ${maxSelections}`);
            return current;
          }
          setValidationError('');
          return [...current, optionValue];
        });
      };

      return (
        <>
          {displayCell}
          {rangeEditorPosition ? ReactDOM.createPortal(
            <div
              className="editable-cell__multi-select"
              style={{
                top: rangeEditorPosition.top,
                left: rangeEditorPosition.left,
                width: rangeEditorPosition.width,
              }}
            >
              <div className="editable-cell__multi-select-title">
                {inputProps.title || 'Choose values'}
              </div>
              <div className="editable-cell__multi-select-list">
                {options.map(option => (
                  <label key={option.value}>
                    <input
                      type="checkbox"
                      checked={multiDraft.includes(option.value)}
                      onChange={() => toggleOption(option.value)}
                    />
                    <span>{option.label || option.value}</span>
                  </label>
                ))}
              </div>
              <div className="editable-cell__range-actions">
                <button type="button" onClick={attemptSave}>Save</button>
                <button type="button" onClick={() => {
                  setMultiDraft([]);
                  setValidationError('');
                }}>Clear</button>
                <button type="button" onClick={cancelEdit}>Cancel</button>
              </div>
              {validationError ? (
                <div className="editable-cell__validation" role="alert">
                  {validationError}
                </div>
              ) : null}
            </div>,
            document.body
          ) : null}
        </>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <input
          type={type}
          value={draft}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-lpignore="true"
          data-form-type="other"
          onChange={e => {
            setDraft(e.target.value);
            if (validationError) setValidationError('');
          }}
          autoFocus
          aria-invalid={validationError ? 'true' : 'false'}
          title={validationError || ''}
          onBlur={() => {
            attemptSave();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              attemptSave();
            }
            else if (e.key === 'Escape') {
              // abort: drop changes
              cancelEdit();
            }
          }}
          {...inputProps}
          style={{ 
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            textAlign: 'center',
            boxSizing: 'border-box',
            borderColor: validationError ? '#c62828' : undefined,
            boxShadow: validationError ? '0 0 0 1px rgba(198, 40, 40, 0.25)' : undefined,
          }}
        />
        {validationError ? (
          <div
            role="alert"
            style={{
              color: '#c62828',
              fontSize: '0.72rem',
              lineHeight: 1.2,
              marginTop: '4px',
              textAlign: 'center',
              whiteSpace: 'normal',
            }}
          >
            {validationError}
          </div>
        ) : null}
      </div>
    );
  }

  return displayCell;
}
